import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useAtom } from 'jotai';

import { RWMemberRoleKeys } from '@restworld/utility-types';

import useCreateEmployerUserContact from './use-create-employer-user-contact';
import { useCollaboratorActionModal, collaboratorActionModalVariantAtom } from '../collaborators.atoms';
import { useEmployerUserContactsListQueryKey } from './use-employer-user-contacts-list-query';
import useCheckUserExistenceMutation from './use-check-user-existence-mutation';

export enum AddCollaboratorSteps {
  FILL_INFO,
  CHOOSE_PERMISSION_LEVEL,
  VIEW_SUCCESS_MESSAGE,
}

function useCollaboratorDetailsForm({ setStep, step }: CollaboratorDetailsFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    clearErrors,
    watch,
    trigger,
  } = useForm<AddCollaboratorFormValues>({
    mode: 'onTouched',
    defaultValues: { email: '', name: '', surname: '', phoneNumber: '' },
  });
  const employerUserContactListQueryKey = useEmployerUserContactsListQueryKey();
  const { mutate, isSuccess, isPending } = useCreateEmployerUserContact();
  const [, setCollaboratorActionModalVariant] = useAtom(collaboratorActionModalVariantAtom);
  const { closeCollaboratorActionModal } = useCollaboratorActionModal();
  const {
    mutate: userExistenceCheckMutation,
    isSuccess: isExistenceCheckSuccessful,
    isPending: isExistenceCheckPending,
    error: userExistenceError,
    reset: resetUserExistenceCheck,
  } = useCheckUserExistenceMutation();
  const checkIsFormValid = () => Object.keys(touchedFields).length > 3 && Object.keys(errors).length === 0; // field state not updated properly when wrapped in useCallback

  const isFormValid = checkIsFormValid();
  const canSubmit = step === AddCollaboratorSteps.CHOOSE_PERMISSION_LEVEL && isFormValid;

  const onSubmit: SubmitHandler<AddCollaboratorFormValues> = useCallback(
    (values) => {
      const { name, surname, email, phoneNumber, permissionLevel } = values;
      mutate({ email, name, surname, phone_number: phoneNumber, role_rw_key: permissionLevel });
    },
    [mutate]
  );

  const hasConflictingEmailError = useMemo(
    () =>
      userExistenceError &&
      'data' in userExistenceError &&
      (userExistenceError.data as { conflicting_fields?: string[] })?.conflicting_fields?.includes('email'),
    [userExistenceError]
  );

  const hasConflictingphoneNumberError = useMemo(
    () =>
      userExistenceError &&
      'data' in userExistenceError &&
      (userExistenceError.data as { conflicting_fields?: string[] })?.conflicting_fields?.includes('phone_number'),
    [userExistenceError]
  );

  const handleCancel = useCallback(() => {
    if (step === AddCollaboratorSteps.FILL_INFO) {
      closeCollaboratorActionModal();
      return;
    }
    clearErrors();
    setStep((prev) => (prev > 0 ? prev - 1 : prev));
  }, [clearErrors, closeCollaboratorActionModal, setStep, step]);

  const handleContinue = useCallback(async () => {
    trigger('email');
    trigger('phoneNumber');
    trigger('name');
    trigger('surname');
    if (step === AddCollaboratorSteps.FILL_INFO && isFormValid) {
      const email = watch('email');
      const phoneNumber = watch('phoneNumber');
      userExistenceCheckMutation({ email, phoneNumber });
    } else {
      resetUserExistenceCheck();
    }
  }, [isFormValid, resetUserExistenceCheck, step, trigger, userExistenceCheckMutation, watch]);

  const handleSubmitForm = useCallback(() => {
    resetUserExistenceCheck();
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit, resetUserExistenceCheck]);

  useEffect(() => {
    if (isSuccess) {
      setCollaboratorActionModalVariant('noHeading');
      setStep(AddCollaboratorSteps.VIEW_SUCCESS_MESSAGE);
    }
  }, [employerUserContactListQueryKey, isSuccess, setCollaboratorActionModalVariant, setStep]);

  useEffect(() => {
    if (isExistenceCheckSuccessful && isFormValid) {
      setStep((prev) => (prev < 1 ? prev + 1 : prev));
      resetUserExistenceCheck();
    }
  }, [isExistenceCheckSuccessful, isFormValid, resetUserExistenceCheck, setStep]);

  return {
    handleSubmit,
    onSubmit,
    handleCancel,
    handleContinue,
    isPending: isPending || isExistenceCheckPending,
    control,
    canSubmit,
    errors,
    checkIsFormValid,
    hasConflictingEmailError,
    hasConflictingphoneNumberError,
    handleSubmitForm,
  };
}
export default useCollaboratorDetailsForm;

export interface CollaboratorDetailsFormProps {
  setStep: Dispatch<SetStateAction<AddCollaboratorSteps>>;
  step: AddCollaboratorSteps;
}
export interface AddCollaboratorFormValues {
  name?: string;
  surname?: string;
  email?: string;
  phoneNumber?: string;
  permissionLevel?: RWMemberRoleKeys;
}
